import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isArabicNumber, toEnglishNumber } from "../utils/arabicConversions";
import { searchAPI } from "../axios/search-axios";
import { toast } from "react-toastify";
import { normalizeSearchRequest, getSubmittedSearchRequest } from "../utils/searchRequest";
import { getTranslatedSearchTerm } from "../utils/searchResponse";
import { trackSearch } from "../utils/searchAnalytics";

const useLeftPane = ({
  setShow,
  setSearchResults,
  setSearchResultSets,
  setAnalytics,
  setCurrentPage,
  setAnalyticsModalOpen,
  setLoading,
  setSearchAfter,
  setTotalCount,
  setSortParam,
  setNextSortType,
  setActiveSort,
  setEffectiveSearchTerm,
}) => {
  const location = useLocation();
  const submittedSearchRequest = useMemo(
    () => getSubmittedSearchRequest(location.state),
    [location.state]
  );

  const [formatVisible, setFormatVisible] = useState(false);
  const [dateVisible, setDateVisible] = useState(false);
  const [locationVisible, setLocationVisible] = useState(false);
  const [collectionVisible, setCollectionVisible] = useState(false);
  const [sourceVisible, setSourceVisible] = useState(false);

  const toggleFormat = () => {
    setFormatVisible(!formatVisible);
  };

  const toggleDate = () => {
    setDateVisible(!dateVisible);
  };

  const toggleLocation = () => {
    setLocationVisible(!locationVisible);
  };

  const toggleCollection = () => {
    setCollectionVisible(!collectionVisible);
  };

  const toggleSource = () => {
    setSourceVisible(!sourceVisible);
  };

  const [fromYear, setFromYear] = useState(null);
  const [toYear, setToYear] = useState(null);

  const handleFromYearChange = (event) => {
    let numericValue = "";
    const inputValue = event.target.value;
    if (isArabicNumber(inputValue)) {
      numericValue = toEnglishNumber(inputValue);
    } else {
      numericValue = inputValue.replace(/\D/g, "");
    }

    const truncatedValue = numericValue.slice(0, 4);
    setFromYear(truncatedValue);

    //Update filters state
    if (truncatedValue !== "")
      setFilters({ ...filters, from_year: truncatedValue });

    //if truncatedValue is empty string, remove from_year from filters
    if (truncatedValue === "") {
      const { from_year, ...rest } = filters;
      setFilters(rest);
    }
    localStorage.setItem("filters", JSON.stringify(filters));
  };

  const handleToYearChange = (event) => {
    let numericValue = "";
    const inputValue = event.target.value;
    if (isArabicNumber(inputValue)) {
      numericValue = toEnglishNumber(inputValue);
    } else {
      numericValue = inputValue.replace(/\D/g, "");
    }

    const truncatedValue = numericValue.slice(0, 4);
    setToYear(truncatedValue);

    if (truncatedValue !== "")
      setFilters({ ...filters, to_year: truncatedValue });

    if (truncatedValue === "") {
      const { to_year, ...rest } = filters;
      setFilters(rest);
    }
    localStorage.setItem("filters", JSON.stringify(filters));
  };

  const lang = localStorage.getItem("lang");
  const collNameKey = lang === "ar" ? "coll_name_arabic" : "coll_name";
  const collLocationKey =
    lang === "ar" ? "coll_location_arabic" : "coll_location";
  const collSourceKey = lang === "ar" ? "coll_source_arabic" : "coll_source";

  const getInitialFilters = () => {
    return {
      ...submittedSearchRequest,
      sort_param: "date",
      sort_type: "asc",
      coll_type: [],
      [collNameKey]: [],
      [collLocationKey]: [],
      [collSourceKey]: [],
    };
  };

  const [filters, setFilters] = useState(getInitialFilters());

  const updateFilters = (nextFilters) => {
    setFilters(nextFilters);
    localStorage.setItem("filters", JSON.stringify(nextFilters));
  };

  const isFilterChecked = (filterKey, value) =>
    Array.isArray(filters[filterKey]) && filters[filterKey].includes(value);

  const handleFormatCheckboxChange = (event, coll_type) => {
    const isChecked = event.target.checked;

    let nextFilters;
    if (isChecked) {
      nextFilters = {
        ...filters,
        coll_type: Array.isArray(filters.coll_type)
          ? [...filters.coll_type, coll_type]
          : [coll_type],
      };
    } else {
      nextFilters = {
        ...filters,
        coll_type: Array.isArray(filters.coll_type)
          ? filters.coll_type.filter((x) => x !== coll_type)
          : [],
      };
    }
    updateFilters(nextFilters);
  };

  const handleCollectionCheckboxChange = (event, collName) => {
    const isChecked = event.target.checked;

    let nextFilters;
    if (isChecked) {
      nextFilters = {
        ...filters,
        [collNameKey]: Array.isArray(filters[collNameKey])
          ? [...filters[collNameKey], collName]
          : [collName],
      };
    } else {
      nextFilters = {
        ...filters,
        [collNameKey]: Array.isArray(filters[collNameKey])
          ? filters[collNameKey].filter((x) => x !== collName)
          : [],
      };
    }
    updateFilters(nextFilters);
  };

  const handleSourceCheckboxChange = (event, collSource) => {
    const isChecked = event.target.checked;

    let nextFilters;
    if (isChecked) {
      nextFilters = {
        ...filters,
        [collSourceKey]: Array.isArray(filters[collSourceKey])
          ? [...filters[collSourceKey], collSource]
          : [collSource],
      };
    } else {
      nextFilters = {
        ...filters,
        [collSourceKey]: Array.isArray(filters[collSourceKey])
          ? filters[collSourceKey].filter((x) => x !== collSource)
          : [],
      };
    }
    updateFilters(nextFilters);
  };

  const handleLocationCheckboxChange = (event, collLocation) => {
    const isChecked = event.target.checked;

    let nextFilters;
    if (isChecked) {
      nextFilters = {
        ...filters,
        [collLocationKey]: Array.isArray(filters[collLocationKey])
          ? [...filters[collLocationKey], collLocation]
          : [collLocation],
      };
    } else {
      nextFilters = {
        ...filters,
        [collLocationKey]: Array.isArray(filters[collLocationKey])
          ? filters[collLocationKey].filter((x) => x !== collLocation)
          : [],
      };
    }
    updateFilters(nextFilters);
  };

  const navigate = useNavigate();
  const buildNoResultsMessage = () => {
    if (lang === "ar") {
      return "لم يتم العثور على نتائج. يرجى توسيع الفلاتر الخاصة بك";
    }

    return "No results found. Please expand your filters";
  };

  const submitFilters = async () => {
    // Stringify the keys of the filters object
    // let filtersStringified = JSON.stringify(filters);
    // Check the filters object. If coll_name is empty array, remove it from the object.
    // Same for coll_location, coll_source, and coll_type

    setLoading(true);

    if (fromYear && toYear && fromYear > toYear) {
      if (lang !== "ar") {
        toast.error("Please enter a valid range of years", {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
        });
      } else {
        toast.error("الرجاء إدخال نطاق سنوات صالح", {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          rtl: true,
        });
      }
      setLoading(false);
      return;
    }

    const latestFilters = {
      ...filters,
      ...submittedSearchRequest,
      sort_param: "date",
      sort_type: "asc",
    };
    updateFilters(normalizeSearchRequest(latestFilters));

    let res = await searchAPI.put("", normalizeSearchRequest(latestFilters), {
      headers: {
        "Content-Type": "application/json",
      },
    });

    trackSearch(latestFilters.search_term);

    const effectiveSearchTerm = latestFilters.enable_translation
      ? getTranslatedSearchTerm(res)
      : "";
    localStorage.setItem(
      "effectiveSearchTerm",
      JSON.stringify(effectiveSearchTerm)
    );
    if (typeof setEffectiveSearchTerm === "function") {
      setEffectiveSearchTerm(effectiveSearchTerm);
    }

    let results = res.data.docs;
    if (results.length === 0) {
      toast.warn(buildNoResultsMessage(), {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        rtl: lang === "ar",
      });
      setLoading(false);
      return;
    }

    res.data.docs.forEach(doc => {
      doc.is_download_blocked = res.data.download_blocked_list.includes(doc.coll_identifier);
    });

    setSearchResults(res.data.docs);
    setSearchResultSets((prevSearchResultSets) => ({
      [1]: res.data.docs,
    }));
    setAnalytics(res.data.analytics);
    setCurrentPage(1);
    if (typeof setSearchAfter === "function") setSearchAfter(res.data.search_after);
    if (typeof setTotalCount === "function") setTotalCount(res.data.count);
    if (typeof setSortParam === "function") setSortParam("date");
    if (typeof setNextSortType === "function") setNextSortType("desc");
    if (typeof setActiveSort === "function") {
      setActiveSort({ param: "date", type: "asc" });
    }

    //Navigate to search results page
    navigate("/search-results", {
      state: {
        searchResults: res.data.docs,
        pageNumber: 1,
        searchString: latestFilters.search_term,
        analytics: res.data.analytics,
        analyticsQueryKey: res.data.analytics_query_key,
        matchType: latestFilters.match_type,
        searchRequest: normalizeSearchRequest(latestFilters),
        effectiveSearchTerm,
        totalCount: res.data.count,
        searchAfter: res.data.search_after,
      },
    });

    setLoading(false);

    if (typeof setShow === "function") setShow(false);
  };

  const clearFilters = async () => {
    setLoading(true);

    //Clear the filters object

    const initialFilters = getInitialFilters();
    updateFilters(normalizeSearchRequest(initialFilters));

    //Uncheck all checkboxes
    let checkboxes = document.querySelectorAll("input[type=checkbox]");
    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    setFromYear("");
    setToYear("");

    //Send empty filters to backend and update everything
    let res = await searchAPI.put("", normalizeSearchRequest(initialFilters));
    trackSearch(initialFilters.search_term);
    const effectiveSearchTerm = initialFilters.enable_translation
      ? getTranslatedSearchTerm(res)
      : "";
    localStorage.setItem(
      "effectiveSearchTerm",
      JSON.stringify(effectiveSearchTerm)
    );
    if (typeof setEffectiveSearchTerm === "function") {
      setEffectiveSearchTerm(effectiveSearchTerm);
    }
    setSearchResults(res.data.docs);
    setSearchResultSets((prevSearchResultSets) => ({
      [1]: res.data.docs,
    }));
    setAnalytics(res.data.analytics);
    setCurrentPage(1);
    if (typeof setSearchAfter === "function") setSearchAfter(res.data.search_after);
    if (typeof setTotalCount === "function") setTotalCount(res.data.count);
    if (typeof setSortParam === "function") setSortParam("date");
    if (typeof setNextSortType === "function") setNextSortType("desc");
    if (typeof setActiveSort === "function") {
      setActiveSort({ param: "date", type: "asc" });
    }
    
    res.data.docs.forEach(doc => {
      doc.is_download_blocked = res.data.download_blocked_list.includes(doc.coll_identifier);
    });

    navigate("/search-results", {
      state: {
        searchResults: res.data.docs,
        pageNumber: 1,
        searchString: initialFilters.search_term,
        analytics: res.data.analytics,
        analyticsQueryKey: res.data.analytics_query_key,
        matchType: initialFilters.match_type,
        searchRequest: normalizeSearchRequest(initialFilters),
        effectiveSearchTerm,
        totalCount: res.data.count,
        searchAfter: res.data.search_after,
      },
    });
    setLoading(false);
    if (typeof setShow === "function") setShow(false);
    // window.location.reload(true);
    // forceUpdate();
  };

  const [showMoreFormat, setShowMoreFormat] = useState(false);
  const [showMoreLocation, setShowMoreLocation] = useState(false);
  const [showMoreCollection, setShowMoreCollection] = useState(false);
  const [showMoreSource, setShowMoreSource] = useState(false);

  const toggleMoreFormat = () => {
    setShowMoreFormat(!showMoreFormat);
  };

  const toggleMoreLocation = () => {
    setShowMoreLocation(!showMoreLocation);
  };

  const toggleMoreCollection = () => {
    setShowMoreCollection(!showMoreCollection);
  };

  const toggleMoreSource = () => {
    setShowMoreSource(!showMoreSource);
  };

  const handleAnalyticsClick = () => {
    setAnalyticsModalOpen(true);
  };

  return {
    formatVisible,
    setFormatVisible,
    dateVisible,
    setDateVisible,
    locationVisible,
    setLocationVisible,
    collectionVisible,
    setCollectionVisible,
    sourceVisible,
    setSourceVisible,
    fromYear,
    setFromYear,
    toYear,
    setToYear,
    filters,
    setFilters,
    isFilterChecked,
    collNameKey,
    collLocationKey,
    collSourceKey,
    toggleFormat,
    toggleDate,
    toggleLocation,
    toggleCollection,
    toggleSource,
    handleFromYearChange,
    handleToYearChange,
    handleFormatCheckboxChange,
    handleCollectionCheckboxChange,
    handleSourceCheckboxChange,
    handleLocationCheckboxChange,
    submitFilters,
    clearFilters,
    showMoreFormat,
    setShowMoreFormat,
    showMoreLocation,
    setShowMoreLocation,
    showMoreCollection,
    setShowMoreCollection,
    showMoreSource,
    setShowMoreSource,
    handleAnalyticsClick,
    toggleMoreFormat,
    toggleMoreLocation,
    toggleMoreCollection,
    toggleMoreSource,
  };
};

export default useLeftPane;
